import { useState } from "react";
import { Alert, Input, Modal, Typography, notification } from "antd";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";
import { apiErrorMessage } from "../api/apiError";

const { Text } = Typography;

type Props = {
  requestId: string;
  /** What the template calls its cancelled state — clients rename it. */
  statusName?: string | null;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  fetch: number;
  setFetch: (fetch: number) => void;
};

/**
 * Calling a case off.
 *
 * The gap this fills was ordinary and long-standing: somebody raises a case by
 * mistake, or the need goes away, and until now the only way to stop it was an
 * administrator soft-deleting the row — which hides the case from the person
 * who raised it and leaves no record of the decision.
 *
 * Three things this dialog insists on:
 *
 * * **A reason.** A cancelled case with no reason is a hole in the record that
 *   somebody has to chase by email six weeks later. The server refuses a blank
 *   one, so the button is disabled rather than the refusal being a surprise.
 * * **Saying what it is not.** Withdrawing is not rejection. An approver
 *   refusing a case is a judgement about its merits; a requester withdrawing it
 *   is a change of mind about whether to ask at all, and the two are counted
 *   separately.
 * * **Saying what survives.** People hesitate over a destructive-sounding
 *   button because they cannot tell whether the answers they typed are about to
 *   disappear. They are not: nothing is deleted here, ever.
 */
const WithdrawModal = ({
  requestId,
  statusName,
  visible,
  setVisible,
  fetch,
  setFetch,
}: Props) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await ApiService.apiMainRequestWithdrawCreate({
        request_id: parseInt(requestId),
        reason: reason.trim(),
      });
      notification.success({ message: t("This case has been withdrawn.") });
      setVisible(false);
      setReason("");
      setFetch(fetch + 1);
    } catch (e: any) {
      // Never `catch { console.log }` on a mutation: the server's refusals here
      // are exactly the ones a person cannot guess from the screen.
      notification.error({
        message: apiErrorMessage(e, t("Something went wrong.")),
      });
    }
    setBusy(false);
  };

  return (
    <Modal
      title={t("Withdraw this case")}
      open={visible}
      onCancel={() => setVisible(false)}
      onOk={submit}
      okText={t("Withdraw it")}
      okButtonProps={{ danger: true, disabled: !reason.trim(), loading: busy }}
      cancelText={t("Keep it open")}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "Nothing is deleted. The case stays on record with everything filled in so far, and moves to {{status}}.",
          { status: statusName || t("a cancelled state") }
        )}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(
          "This is not the same as rejecting it. Withdrawing means you no longer need it — it is not a decision about whether it should have been approved."
        )}
      </Text>
      <Input.TextArea
        rows={3}
        style={{ marginTop: 12 }}
        value={reason}
        placeholder={t("Why are you withdrawing it?")}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
};

export default WithdrawModal;
